import { CopyId } from "@/components/copy-id";
import { CreditLedger } from "@/components/credit-ledger";
import { useCanWriteHere } from "@/contexts/area.context";
import {
  CREDITS_PER_BRL,
  CreditsDialog,
  creditsToCentavos,
} from "@/components/credits-dialog";
import { SiteHeader } from "@/components/site-header";
import { UserPicker } from "@/components/user-picker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { type Club, clubsProvider } from "@/providers/clubs.provider";
import type { User } from "@/providers/users.provider";
import { formatBRL } from "@/lib/formatters";
import { queryKeys } from "@/lib/queryKeys";
import { adminProvider } from "@/providers/admin.provider";
import {
  IconAlertTriangle,
  IconCoins,
  IconUsersGroup,
  IconWorld,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

function ClubPicker({
  selected,
  onSelect,
}: {
  selected: Club | null;
  onSelect: (club: Club | null) => void;
}) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(id);
  }, [query]);

  const { data: results, isFetching } = useQuery({
    queryKey: ["clubs-search", debouncedQuery],
    queryFn: () => clubsProvider.list(1, debouncedQuery),
    enabled: debouncedQuery.length >= 1,
    staleTime: 30_000,
  });

  if (selected) {
    return (
      <div className="flex items-center justify-between rounded-lg border px-3 py-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{selected.club_name}</p>
          <p className="truncate text-xs text-muted-foreground tabular-nums">
            {selected.member_count.toLocaleString()} members
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="shrink-0 text-xs"
          onClick={() => {
            onSelect(null);
            setQuery("");
          }}
        >
          Change
        </Button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Input
        placeholder="Search clubs by name…"
        value={query}
        autoComplete="off"
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && query.trim().length >= 1 && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border bg-popover shadow-md">
          {isFetching ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">Searching…</p>
          ) : !results?.data.length ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              No clubs found
            </p>
          ) : (
            <div className="max-h-48 overflow-y-auto">
              {results.data.map((c) => (
                <button
                  key={c.club_id}
                  type="button"
                  className="flex w-full flex-col px-3 py-2 text-left hover:bg-accent"
                  onClick={() => {
                    onSelect(c);
                    setOpen(false);
                  }}
                >
                  <span className="text-sm">{c.club_name}</span>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {c.member_count.toLocaleString()} members
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function UserCreditsPanel() {
  const canWrite = useCanWriteHere();
  const [picked, setPicked] = useState<User | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <div className="flex flex-col gap-4 rounded-lg border p-4">
      <div className="flex flex-col gap-2">
        <Label>User</Label>
        <UserPicker selected={picked} onSelect={setPicked} />
      </div>

      {picked && (
        <div className="grid grid-cols-1 gap-x-4 gap-y-2.5 rounded-lg border p-3 text-sm sm:grid-cols-2">
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Username</p>
            <p className="font-medium truncate">{picked.username}</p>
          </div>
          <div className="min-w-0 break-all">
            <p className="text-xs text-muted-foreground">User ID</p>
            <CopyId id={picked.user_id} />
          </div>
        </div>
      )}

      {canWrite && (
        <>
          <Button
            className="w-fit gap-1.5"
            disabled={!picked}
            onClick={() => setDialogOpen(true)}
          >
            <IconCoins className="size-4" />
            Adjust credits
          </Button>

          <p className="text-xs text-muted-foreground">
            The user is notified only if the balance actually goes up. Every
            adjustment is recorded in the user's credit ledger with your admin
            ID and note.
          </p>
        </>
      )}

      {picked && (
        <div className="flex flex-col gap-2">
          <Label>Credit ledger</Label>
          <CreditLedger key={picked.user_id} userId={picked.user_id} />
        </div>
      )}

      {picked && canWrite && (
        <CreditsDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          target={{ kind: "user", id: picked.user_id, name: picked.username }}
        />
      )}
    </div>
  );
}

function ClubCreditsPanel() {
  const [picked, setPicked] = useState<Club | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <div className="flex flex-col gap-4 rounded-lg border p-4">
      <div className="flex flex-col gap-2">
        <Label>Club</Label>
        <ClubPicker selected={picked} onSelect={setPicked} />
      </div>

      {picked && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 rounded-lg border p-3 text-sm">
          <div>
            <p className="text-xs text-muted-foreground">Club</p>
            <p className="font-medium truncate">{picked.club_name}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Club ID</p>
            <CopyId id={picked.club_id} />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Members</p>
            <p className="font-medium tabular-nums">
              {picked.member_count.toLocaleString()}
            </p>
          </div>
        </div>
      )}

      <Button
        className="w-fit gap-1.5"
        disabled={!picked}
        onClick={() => setDialogOpen(true)}
      >
        <IconUsersGroup className="size-4" />
        Adjust member credits
      </Button>

      <p className="text-xs text-muted-foreground">
        Applies to every member — owner, admins and members alike; deleted
        accounts are excluded. There's no preview and no undo, so a mistyped
        "set to exactly" can only be recovered from a database restore.
        Notifications go out in the background to members whose balance rose.
      </p>

      {picked && (
        <CreditsDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          target={{
            kind: "club",
            id: picked.club_id,
            name: picked.club_name,
            memberCount: picked.member_count,
          }}
        />
      )}
    </div>
  );
}

function AllUsersCreditsPanel() {
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: totalUsers } = useQuery({
    queryKey: queryKeys.adminUsersCount(),
    queryFn: () => adminProvider.getUsersCount(),
    staleTime: 60_000,
  });

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-destructive/40 p-4">
      <div className="flex items-start gap-2">
        <IconAlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
        <p className="text-sm">
          Grants credits to <span className="font-medium">every</span>{" "}
          non-deleted account on the platform.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 rounded-lg border p-3 text-sm">
        <div>
          <p className="text-xs text-muted-foreground">Estimated audience</p>
          <p className="font-medium tabular-nums">
            {totalUsers !== undefined
              ? `${totalUsers.toLocaleString()} accounts`
              : "…"}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Cost</p>
          <p className="font-medium tabular-nums">
            {CREDITS_PER_BRL.toLocaleString()} credits ≈ R$ 1 per user
          </p>
        </div>
      </div>

      <Button
        variant="destructive"
        className="w-fit gap-1.5"
        onClick={() => setDialogOpen(true)}
      >
        <IconWorld className="size-4" />
        Adjust everyone's credits
      </Button>

      <p className="text-xs text-muted-foreground">
        There is no dry-run and no undo — a mistyped "set to exactly" here can
        only be recovered from a database restore. You'll be asked to type
        ALL_USERS before it goes out. Notifications are queued and delivered in
        the background.
      </p>

      <CreditsDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        target={{ kind: "all", estimatedAudience: totalUsers }}
      />
    </div>
  );
}

export function CreditsPage() {
  const canWrite = useCanWriteHere();

  return (
    <div className="flex h-full flex-col">
      <SiteHeader title="Credits" />

      <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-4 lg:px-6 lg:py-6">
        <p className="max-w-2xl text-sm text-muted-foreground">
          {canWrite
            ? `Adjust credit balances for a single user or for every member of a club, and review any user's credit ledger. "Raise to at least" is safe to repeat; "Add" grants again on every send; "Set to exactly" overwrites and can lower balances.`
            : "Review any user's credit ledger. You have read-only access, so adjusting balances is unavailable."}
        </p>

        <Tabs defaultValue="user" className="max-w-2xl">
          <TabsList>
            <TabsTrigger value="user">User</TabsTrigger>
            {canWrite && <TabsTrigger value="club">Club</TabsTrigger>}
            {canWrite && <TabsTrigger value="all">All users</TabsTrigger>}
          </TabsList>

          <TabsContent value="user" className="mt-4">
            <UserCreditsPanel />
          </TabsContent>

          {canWrite && (
            <TabsContent value="club" className="mt-4">
              <ClubCreditsPanel />
            </TabsContent>
          )}

          {canWrite && (
            <TabsContent value="all" className="mt-4">
              <AllUsersCreditsPanel />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}

export default CreditsPage;
